<script lang="ts" setup>
const t2 = useT2({
  'Bad value': 'Neteisinga reikšmė',
  'Missing values': 'Trūksta reikšmių',
  'Please check your email': 'Patikrinkite savo el. paštą',
  "We've sent a code to:": 'Mes išsiuntėme kodą adresu:',
  'Verification code:': 'Užklausos kodas:',
  'Confirmation code:': 'Patvirtinimo kodas:',
  "Didn't receive an email?": 'Negavote el. laiško?',
  Resend: 'Išsiųsti dar kartą',
  Cancel: 'Atšaukti',
})

const props = defineProps<{
  codeRef: gen.InputAuthCodeRef | None
  email: string
  label: stringI18n
  cancel(): void
  save(code: string): void
  notify: ReturnType<typeof useNotify>
  emailInvalid?: (i: boolean) => void
}>()

const timer = useTimeout()
const elemMap = useRefsMap<HTMLInputElement, number>()

function getInputElem(el: HTMLElement | EventTarget | None) {
  if (el instanceof HTMLInputElement) return el
  if (el && 'setSelectionRange' in el) return el as HTMLInputElement
}

function getInputs(index: 'all' | number, cb: (elem: HTMLInputElement, index: number) => void) {
  const go = (i: number) => {
    const el = getInputElem(elemMap.get(i))
    if (el) cb(el, i)
  }
  if (index === 'all') helpers.repeat(6, (index) => go(index))
  else go(index)
}

function getInputCode(): string | null {
  const value: string[] = []
  getInputs('all', (el) => el?.value?.length === 1 && value.push(el.value))
  let error = value.length !== 6 ? t2('Bad value') : value.some((i) => !i?.length) ? t2('Missing values') : undefined
  const str = value.join('')
  if (!error && str.length !== 6) error = t2('Bad value')
  if (error) {
    props.notify.error(t2('Bad value'))
    return null
  }
  return str
}

function saveAuto() {
  timer.set(() => {
    const code = getInputCode()
    if (code) props.save(code)
  }, 0)
}

function textSelect(elem: EventTarget | HTMLElement | None) {
  timer.set(() => getInputElem(elem)?.setSelectionRange(0, -1, 'forward'), 0)
}

function focusInput(index: number) {
  getInputs(index, (el) => {
    el.focus({ preventScroll: true })
    el.setSelectionRange(0, -1, 'forward')
  })
}

function setValue(index: number, value: string) {
  getInputs(index, (el) => (el.value = value))
}

type Command = { command: 'set' | 'backspace' | 'right' | 'left'; value: string; priority: number; index: number }

function executeCommnad(best: Command) {
  switch (best.command) {
    case 'backspace': {
      setValue(best.index, '')
      focusInput(best.index - 1)
      return
    }
    case 'set': {
      setValue(best.index, best.value)
      if (best.value) {
        if (best.index === 5) saveAuto()
        else focusInput(best.index + 1)
      }
      return
    }
    case 'left': {
      focusInput(best.index - 1)
      return
    }
    case 'right': {
      focusInput(best.index + 1)
      return
    }
    default: {
      return helpers.never(best.command, () => undefined)
    }
  }
}

let commands: Command[] = []
const timerCommand = useTimeout()
function commandSet(command: Command) {
  commands.push(command)
  timerCommand.set(() => {
    const copy = [...commands]
    commands = []
    let best: Command | undefined
    for (const command of copy) {
      if (!best?.priority || command.priority > best.priority) best = command
    }
    if (best?.command) executeCommnad(best)
  }, 10)
}

const charValid = (c: string | None): c is string => c?.length === 1 && /^[a-z0-9]+$/i.test(c)

function keypress(e: KeyboardEvent | Event, index: number): void {
  let prevent = () => {
    e.preventDefault()
    e.stopImmediatePropagation()
    e.stopPropagation()
    prevent = () => {
      //
    }
  }

  function command(c: Command) {
    prevent()
    commandSet(c)
  }

  if ('key' in e) {
    const key = e.key
    if (key === 'Backspace') return command({ command: 'backspace', index, priority: 5, value: '' })
    if (key === 'ArrowRight') return command({ command: 'right', index, priority: 4, value: '' })
    if (key === 'ArrowLeft') return command({ command: 'left', index, priority: 4, value: '' })
    if (key.length > 1 || e.shiftKey || e.metaKey || e.altKey || e.ctrlKey) return
  }

  prevent()

  const getValue = (): { char: string; priority: number } | undefined => {
    if ('key' in e && charValid(e.key)) {
      return { char: e.key, priority: 5 }
    }

    if ('target' in e && e.target && 'value' in e.target) {
      const target = e.target.value
      const char = typeof target === 'string' ? target.at(-1) : typeof target === 'number' ? target.toString() : undefined
      if (charValid(char)) return { char, priority: e instanceof InputEvent ? 7 : 6 }
    }
  }

  const value = getValue()
  if (charValid(value?.char)) return command({ command: 'set', index, priority: value.priority, value: value.char })
  else return command({ command: 'set', index, priority: 0, value: '' })
}

function onPaste(e: ClipboardEvent) {
  e.preventDefault()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const paste = (e.clipboardData || (window as any)?.clipboardData)?.getData?.('text')
  const paste2 = typeof paste === 'string' ? paste.replace(/\s/g, '') : undefined
  if (paste2?.length === 6) {
    getInputs('all', (elem, index) => (elem.value = paste2[index] ?? ''))
    focusInput(5)
    saveAuto()
  }
}

useDelayInit(200, () => focusInput(0))

const data = shallowReactive({
  regen: undefined as undefined | Date,
  regenAllowed: false,
  regenCountdown: false,
  emailInvalidCache: { email: '', invalid: false },
})

const invalidEmail = computed(() => {
  const invalid = data.emailInvalidCache
  const email = props.email
  return invalid.email === email && invalid.invalid
})

const timerRegen = useTimeout()
const [codeRefUi, codeRefSet] = useValueSynced2(() => props.codeRef)
watch(
  () => codeRefUi.value?._id,
  (ok) => {
    if (!ok) return
    data.regenAllowed = false
    data.regen = helpers.dateUpdate(new Date(), 'future', 'seconds', 30)
    timerRegen.set(() => (data.regenAllowed = true), 30 * 1000)
  },
  { immediate: true }
)

const regenDisabled = computed(() => data.regenCountdown && !data.regenAllowed)

async function resent() {
  if (!data.regenAllowed) {
    data.regenCountdown = true
    return null
  }
  const codeRef = codeRefUi.value
  if (!codeRef) return null
  const res = await useBuyerAuthUpdate({ authCodeResend: { _id: codeRef._id, codeRefNumber: codeRef.codeRefNumber }, widget: 'authCode' })
  data.emailInvalidCache = { invalid: res.data?.buyerAuth?.emailInvalid === true, email: props.email }
  props.emailInvalid?.(data.emailInvalidCache.invalid)
  if (res.data?.buyerAuth.codeRefNumber) codeRefSet(res.data?.buyerAuth.codeRefNumber)
  return res
}
</script>

<template>
  <div class="_col _pt1 _pb1">
    <UiUpper class="_pb1" :title="t2('Please check your email')" />

    <UiLine :label="t2(`We've sent a code to:`)" :value="email" :class-value="invalidEmail ? $style.error : undefined" :color="invalidEmail ? 'red' : undefined" />
    <UiLine :label="t2('Verification code:')" :value="codeRefUi?.codeRefNumber" />
    <div class="_row _wrap _ctrY _gap1 _pt1 _pb04">
      <div class="_label">{{ t2('Confirmation code:') }}</div>
      <UiMsg v-if="notify.notify.value.error" red :txt="notify.notify.value.error" />
    </div>

    <div :class="$style.inputs">
      <template v-for="nr in 6" :key="nr">
        <input
          :ref="(el: HTMLElement) => elemMap.set(nr - 1, el)"
          type="text"
          :class="$style.input"
          maxlength="1"
          autocorrect="off"
          name="2fa-code"
          autocomplete="one-time-code"
          autocapitalize="none"
          spellcheck="false"
          :aria-label="'Digit ' + nr"
          placeholder=""
          :autofocus="nr === 1 ? 'true' : 'false'"
          @keydown="(e: KeyboardEvent) => keypress(e, nr - 1)"
          @keypress="(e: KeyboardEvent) => keypress(e, nr - 1)"
          @input="(e: Event) => keypress(e, nr - 1)"
          @focus.passive="(e: Event) => textSelect(e.target)"
          @paste="(e: ClipboardEvent) => onPaste(e)"
        />
        <span v-if="nr === 3" :class="$style.split" />
      </template>

      <div v-if="notify.notify.value.isLoading" class="_row _ctrY">
        <UiLoading />
      </div>
    </div>

    <div class="_row _wrap _gap1 _pt2">
      <slot :get-input-code="getInputCode">
        <ButtonAction color :txt="label" :submit="() => unvoid(saveAuto())" />
      </slot>
      <ButtonAction :txt="t2('Cancel')" :submit="() => unvoid(cancel())" />

      <div v-if="codeRefUi" class="_italic _mla _row _wrap _ctrY _gap08 _end">
        <UiCountdown v-if="data.regenCountdown && data.regen && !data.regenAllowed" class="_mono" :date="data.regen" />
        <template v-else>{{ t2(`Didn't receive an email?`) }}</template>
        <ButtonAction :txt="t2('Resend')" :disabled="regenDisabled" :submit="() => resent()" />
      </div>
    </div>
  </div>
</template>

<style module>
.error {
  composes: global(_red);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.inputs {
  composes: global(_gap06);
  composes: global(_pl04);

  composes: global(_grid);
  grid-template-columns: repeat(3, 40px) 10px repeat(3, 40px) 30px;
}
.split {
  width: 10px;
  composes: global(_noflex);
}
.input {
  composes: global(_h50);
  composes: global(_br9);
  composes: global(_fw-l);
  composes: global(_b5);
  composes: global(_txt_ctr);
  composes: global(_wmin0);

  color: var(--blue2);
  font-size: 16px;
}
.input:focus {
  background: var(--bg);
  outline-color: rgba(16, 124, 136, 0.1);
  /* box-shadow: 0 0 0 3px var(--blue8);
  border-color: var(--blue7); */
  border: 1px solid var(--blue3);
  box-shadow: 0 0 0 3px hsla(240, 100%, 33.9%, 0.1);
}

.resent {
  composes: global(_fw-xl);
  composes: global(_ml1);

  composes: global(_c1);
  font-style: normal;
}
</style>
